import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Image as ImageIcon,
  Video,
  Plus,
  Search,
  MoreVertical,
  Trash2,
  Clock,
  HardDrive,
  Layout,
  Upload,
  FileVideo,
  FileImage,
  Sparkles,
  Link as LinkIcon,
  CloudUpload,
  X,
} from 'lucide-react';

const SAMPLE_CONTENT = [
  {
    url: 'https://images.unsplash.com/photo-1572015814739-6624032ea483?w=800',
    thumbnail_url: 'https://images.unsplash.com/photo-1572015814739-6624032ea483?w=400',
    category: 'Fashion',
  },
  {
    url: 'https://images.unsplash.com/photo-1770404645621-9828d9b07f2a?w=800',
    thumbnail_url: 'https://images.unsplash.com/photo-1770404645621-9828d9b07f2a?w=400',
    category: 'Food',
  },
  {
    url: 'https://images.unsplash.com/photo-1765555648975-eb94acddb480?w=800',
    thumbnail_url: 'https://images.unsplash.com/photo-1765555648975-eb94acddb480?w=400',
    category: 'Automotive',
  },
];

export default function ContentPage() {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'image',
    url: '',
    thumbnail_url: '',
    duration: 10,
    file_size: 0,
    resolution: '1920x1080',
    tags: [],
  });

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await api.get('/content');
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        toast.error('Please select an image or video file');
        return;
      }
      
      setSelectedFile(file);
      setFormData({
        ...formData,
        name: file.name.split('.')[0],
        type: isImage ? 'image' : 'video',
        file_size: file.size,
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      
      const response = await api.post('/upload', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });
      
      // Create content entry with uploaded file URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backendUrl}${response.data.url}`;
      
      await api.post('/content', {
        ...formData,
        url: fullUrl,
        thumbnail_url: fullUrl,
        file_size: response.data.size,
      });
      
      toast.success('Content uploaded successfully');
      setDialogOpen(false);
      resetForm();
      loadContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadMode === 'file') {
      await handleUpload();
      return;
    }
    
    try {
      await api.post('/content', formData);
      toast.success('Content added successfully');
      setDialogOpen(false);
      resetForm();
      loadContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await api.delete(`/content/${contentId}`);
      toast.success('Content deleted successfully');
      loadContent();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'image',
      url: '',
      thumbnail_url: '',
      duration: 10,
      file_size: 0,
      resolution: '1920x1080',
      tags: [],
    });
    setSelectedFile(null);
    setUploadMode('url');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'image':
        return <FileImage className="w-5 h-5 text-blue-500" />;
      case 'video':
        return <FileVideo className="w-5 h-5 text-purple-500" />;
      case 'widget':
        return <Sparkles className="w-5 h-5 text-amber-500" />;
      default:
        return <ImageIcon className="w-5 h-5 text-zinc-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredContent = content.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Content Library
          </h1>
          <p className="text-zinc-400 mt-1">Manage your media assets</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-500 btn-glow"
          data-testid="add-content-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Add Content
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white"
            data-testid="search-content"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800 text-white" data-testid="content-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="widget">Widgets</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content Grid */}
      {filteredContent.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
          <h3 className="text-xl font-semibold text-white mb-2">No content found</h3>
          <p className="text-zinc-400 mb-6">
            {searchQuery || typeFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Start building your content library'}
          </p>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <Upload className="w-4 h-4 mr-2" />
            Add Content
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredContent.map((item) => (
            <Card
              key={item.id}
              className="glass-card border-zinc-800 card-hover overflow-hidden group"
              data-testid={`content-card-${item.id}`}
            >
              <div className="aspect-video relative bg-zinc-800">
                {item.thumbnail_url || item.url ? (
                  <img
                    src={item.thumbnail_url || item.url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full items-center justify-center absolute inset-0 bg-zinc-800" style={{ display: item.thumbnail_url || item.url ? 'none' : 'flex' }}>
                  {getTypeIcon(item.type)}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                  <div className="flex-1">
                    <p className="text-white font-medium truncate">{item.name}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-white">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                      <DropdownMenuItem
                        onClick={() => handleDelete(item.id)}
                        className="text-rose-400 focus:text-rose-400 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="absolute top-2 left-2">
                  <Badge variant="outline" className="bg-black/50 backdrop-blur-sm border-white/10 text-white text-[10px]">
                    {item.type}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {item.duration}s
                  </div>
                  <div className="flex items-center gap-1">
                    <Layout className="w-3 h-3" />
                    {item.resolution}
                  </div>
                  {item.file_size > 0 && (
                    <div className="flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      {formatFileSize(item.file_size)}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sample Content Section */}
      {content.length === 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Sample Content
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {SAMPLE_CONTENT.map((sample, index) => (
              <Card
                key={index}
                className="glass-card border-zinc-800 card-hover overflow-hidden cursor-pointer"
                onClick={() => {
                  setFormData({
                    ...formData,
                    name: `${sample.category} Ad`,
                    url: sample.url,
                    thumbnail_url: sample.thumbnail_url,
                    type: 'image',
                  });
                  setUploadMode('url');
                  setDialogOpen(true);
                }}
              >
                <div className="aspect-video relative">
                  <img
                    src={sample.thumbnail_url}
                    alt={sample.category}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Button variant="outline" className="border-white/20 text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Use Template
                    </Button>
                  </div>
                  <Badge className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-sm border-white/10">
                    {sample.category}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add Content Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Add Content
            </DialogTitle>
          </DialogHeader>
          
          {/* Upload Mode Tabs */}
          <Tabs value={uploadMode} onValueChange={setUploadMode} className="w-full">
            <TabsList className="w-full bg-zinc-800">
              <TabsTrigger value="url" className="flex-1 data-[state=active]:bg-zinc-700">
                <LinkIcon className="w-4 h-4 mr-2" />
                URL
              </TabsTrigger>
              <TabsTrigger value="file" className="flex-1 data-[state=active]:bg-zinc-700">
                <CloudUpload className="w-4 h-4 mr-2" />
                Upload File
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="url">
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Content Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="Summer Sale Banner"
                    required
                    data-testid="content-name-input"
                  />
                </div>

                <div>
                  <Label className="text-zinc-300">Content Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="content-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="widget">Widget</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-300">Media URL</Label>
                  <Input
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="https://example.com/image.jpg"
                    required
                    data-testid="content-url-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Duration (seconds)</Label>
                    <Input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Resolution</Label>
                    <Select
                      value={formData.resolution}
                      onValueChange={(value) => setFormData({ ...formData, resolution: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="1920x1080">1920x1080 (FHD)</SelectItem>
                        <SelectItem value="2560x1440">2560x1440 (2K)</SelectItem>
                        <SelectItem value="3840x2160">3840x2160 (4K)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-500" data-testid="save-content-btn">
                    Add Content
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
            
            <TabsContent value="file">
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Content Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    placeholder="Summer Sale Banner"
                    required
                  />
                </div>
                
                {/* File Drop Zone */}
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                    selectedFile ? 'border-blue-500 bg-blue-500/5' : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      handleFileSelect({ target: { files: [file] } });
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  
                  {selectedFile ? (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto rounded-lg bg-blue-500/20 flex items-center justify-center">
                        {formData.type === 'video' ? (
                          <FileVideo className="w-8 h-8 text-blue-500" />
                        ) : (
                          <FileImage className="w-8 h-8 text-blue-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-zinc-500">{formatFileSize(selectedFile.size)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                        className="text-zinc-400 hover:text-white"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <CloudUpload className="w-12 h-12 mx-auto text-zinc-500" />
                      <div>
                        <p className="text-white font-medium">Drop your file here</p>
                        <p className="text-sm text-zinc-500">or click to browse</p>
                      </div>
                      <p className="text-xs text-zinc-600">Supports: JPG, PNG, GIF, WebP, MP4, WebM</p>
                    </div>
                  )}
                </div>
                
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">Uploading...</span>
                      <span className="text-zinc-400">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Duration (seconds)</Label>
                    <Input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Resolution</Label>
                    <Select
                      value={formData.resolution}
                      onValueChange={(value) => setFormData({ ...formData, resolution: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="1920x1080">1920x1080 (FHD)</SelectItem>
                        <SelectItem value="2560x1440">2560x1440 (2K)</SelectItem>
                        <SelectItem value="3840x2160">3840x2160 (4K)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpload} 
                    className="bg-blue-600 hover:bg-blue-500" 
                    disabled={!selectedFile || uploading}
                    data-testid="upload-content-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Content
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
